/**
 * Native Platform Utilities for Capacitor
 * Provides platform detection and native feature initialization
 */

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { logger } from './logger';

/**
 * Platform detection utilities
 */
export const platform = {
  isNative: Capacitor.isNativePlatform(),
  isIOS: Capacitor.getPlatform() === 'ios',
  isAndroid: Capacitor.getPlatform() === 'android',
  isWeb: Capacitor.getPlatform() === 'web',
  name: Capacitor.getPlatform()
};

/**
 * true when Stripe-specific payment UI should be hidden.
 * Stripe never works on native — RevenueCat (IAP) is used instead.
 * Use this to guard Stripe checkout flows, proration modals, and
 * Stripe portal links. Do NOT use to hide the plan grid or upgrade
 * buttons — those work on native via RevenueCat.
 */
export const hideStripePaymentUI = platform.isNative;

/**
 * Point the native status bar at the app's current theme.
 *
 * Capacitor names the style after the *content*, not the background:
 * `Style.Dark` draws light glyphs for a dark surface, `Style.Light` draws dark
 * glyphs for a light one. Getting that backwards is invisible on web and only
 * shows up as unreadable status text on device.
 *
 * The background comes from the live `--bg-primary` so the status bar always
 * matches the canvas the phone shell actually paints, rather than a brand
 * colour that no screen uses.
 *
 * Called on startup and again from `setColorTheme`, so switching appearance at
 * runtime repaints native chrome too.
 */
export const syncStatusBarWithTheme = async (resolvedTheme) => {
  if (!platform.isNative) return;

  try {
    await StatusBar.setStyle({
      style: resolvedTheme === 'dark' ? Style.Dark : Style.Light,
    });

    if (platform.isAndroid) {
      // iOS has no settable status bar background; Android does.
      const canvas = getComputedStyle(document.documentElement)
        .getPropertyValue('--bg-primary')
        .trim();
      if (canvas) await StatusBar.setBackgroundColor({ color: canvas });
    }
  } catch (error) {
    logger.error('Error syncing StatusBar with theme', error);
  }
};

const initStatusBar = async () => {
  if (!platform.isNative) return;
  const resolvedTheme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
  await syncStatusBarWithTheme(resolvedTheme);
  logger.info('StatusBar initialized');
};

/**
 * Configure keyboard behavior
 */
const initKeyboard = async () => {
  if (!platform.isNative) return;

  try {
    // Listen for keyboard events
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.classList.add('keyboard-visible');
      document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-visible');
      document.documentElement.style.setProperty('--keyboard-height', '0px');
    });

    logger.info('Keyboard listeners initialized');
  } catch (error) {
    logger.error('Error initializing Keyboard', error);
  }
};

/**
 * Handle app state changes (background/foreground)
 */
const initAppState = async () => {
  // console.log('[AppState] Initializing app state listener');
  if (!platform.isNative) return;

  try {
    App.addListener('appStateChange', ({ isActive }) => {
      // console.log('[AppState] State changed, isActive:', isActive);
      logger.info('App state changed', { isActive });

      if (isActive) {
        // App came to foreground
        document.dispatchEvent(new CustomEvent('app:resume'));
      } else {
        // App went to background
        document.dispatchEvent(new CustomEvent('app:pause'));
      }
    });

    logger.info('App state listeners initialized');
  } catch (error) {
    logger.error('Error initializing app state', error);
  }
};

/**
 * Hide splash screen
 */
export const hideSplashScreen = async () => {
  if (!platform.isNative) return;

  try {
    await SplashScreen.hide();
    logger.info('Splash screen hidden');
  } catch (error) {
    logger.error('Error hiding splash screen', error);
  }
};

/**
 * Initialize all native features
 * Call this early in app initialization
 */
export const initNativeFeatures = async () => {
  // console.log('[Native] initNativeFeatures called, platform:', platform);

  if (!platform.isNative) {
    // console.log('[Native] Skipping - not native platform');
    logger.info('Running on web platform, skipping native initialization');
    return;
  }

  // console.log('[Native] Starting native initialization');
  logger.info('Initializing native features', { platform: platform.name });

  await Promise.all([
    initStatusBar(),
    initKeyboard(),
    initAppState()
  ]);

  logger.info('Native features initialized');
};

/**
 * Get safe area insets (for notches, home indicators)
 * Note: CSS env() variables are usually sufficient, but this provides JS access
 */
export const getSafeAreaInsets = () => {
  // getPropertyValue() cannot resolve env() expressions from CSS custom properties,
  // so we use a temp element to read the computed values directly.
  const temp = document.createElement('div');
  temp.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'pointer-events:none',
    'visibility:hidden',
    'padding-top:env(safe-area-inset-top, 0px)',
    'padding-bottom:env(safe-area-inset-bottom, 0px)',
    'padding-left:env(safe-area-inset-left, 0px)',
    'padding-right:env(safe-area-inset-right, 0px)',
  ].join(';');
  document.body.appendChild(temp);
  const computed = getComputedStyle(temp);
  const insets = {
    top: parseFloat(computed.paddingTop) || 0,
    bottom: parseFloat(computed.paddingBottom) || 0,
    left: parseFloat(computed.paddingLeft) || 0,
    right: parseFloat(computed.paddingRight) || 0,
  };
  document.body.removeChild(temp);
  return insets;
};
